import { Examination_Scoring_Type } from "@generated/graphql";
import { Add, DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import FieldLabel from "components/FieldLabel";
import TextFieldControl from "components/form/TextFieldControl";
import { SCORING_TYPE_TITLE } from "constants/global";
import { FocusEvent, useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { palette } from "theme";
import { PassingThresholdInput, ResultAssessmentInput } from "../types";
import PassingThresholdControl from "./PassingThresholdControl";
import {
  addAssessmentLevel,
  canAddAssessmentLevel,
  getLevelTitle,
  MAX_ASSESSMENT_LEVELS,
  MIN_ASSESSMENT_LEVELS,
  removeAssessmentLevel,
  syncAssessmentLevels,
} from "./resultAssessmentMode.utils";

type ResultAssessmentModeControlInputs = ResultAssessmentInput & PassingThresholdInput;

const MIN_LEVEL_DIFF = 2;

interface ResultAssessmentModeControlProps {
  isEditing: boolean;
}

const ResultAssessmentModeControlProps = ({ isEditing }: ResultAssessmentModeControlProps) => {
  const { control, watch, setValue, getValues } = useFormContext<ResultAssessmentModeControlInputs>();
  const { fields } = useFieldArray({
    control,
    name: "gradeDeterminationMap",
    keyName: "levelId",
  });

  const scoringType = watch("scoringType");
  const levels = watch("gradeDeterminationMap") || [];
  const isGradeDefinitionMode = scoringType === Examination_Scoring_Type.GradeDetermination;

  useEffect(() => {
    if (!levels.length) return;
    const syncedLevels = syncAssessmentLevels(levels);

    syncedLevels.forEach((level, index) => {
      if (level.from !== levels[index]?.from) {
        setValue(`gradeDeterminationMap.${index}.from`, level.from, { shouldDirty: false });
      }
      if (level.to !== levels[index]?.to) {
        setValue(`gradeDeterminationMap.${index}.to`, level.to, { shouldValidate: isGradeDefinitionMode });
      }
    });
  }, [isGradeDefinitionMode, levels, setValue]);

  const canAddLevel = canAddAssessmentLevel(levels);

  const handleAddLevel = () => {
    const currentLevels = getValues("gradeDeterminationMap") || [];
    setValue("gradeDeterminationMap", addAssessmentLevel(currentLevels), { shouldDirty: true, shouldValidate: true });
  };

  const handleDeleteLevel = (index: number) => {
    const currentLevels = getValues("gradeDeterminationMap") || [];
    setValue("gradeDeterminationMap", removeAssessmentLevel(currentLevels, index), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const applyToValueRules = (index: number, parsedValue: number) => {
    const currentLevelFrom = Number(levels[index]?.from ?? 0);
    const minToValue = Math.min(currentLevelFrom + MIN_LEVEL_DIFF, 100);
    const normalizedValue = Math.max(minToValue, Math.min(100, parsedValue));

    setValue(`gradeDeterminationMap.${index}.to`, normalizedValue, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const nextLevelIndex = index + 1;
    if (nextLevelIndex < levels.length) {
      const nextLevelFrom = Math.min(normalizedValue + 1, 100);
      const nextLevelTo = Number(levels[nextLevelIndex]?.to);

      setValue(`gradeDeterminationMap.${nextLevelIndex}.from`, nextLevelFrom, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (Number.isFinite(nextLevelTo) && nextLevelTo - nextLevelFrom < MIN_LEVEL_DIFF) {
        setValue(`gradeDeterminationMap.${nextLevelIndex}.to`, "" as unknown as number, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  };

  const handleToValueBlur = (index: number, event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const parsedValue = Number(event.target.value);
    if (Number.isNaN(parsedValue)) return;

    applyToValueRules(index, parsedValue);
  };

  const handleToValueByArrows = (index: number, value: number) => {
    if (Number.isNaN(value)) return;

    applyToValueRules(index, value);
  };

  const getSegmentWidth = (from: number, to: number) => {
    const points = Math.max(to - from + 1, 0);
    return `${(points / 101) * 100}%`;
  };

  return (
    <Stack spacing={2.5}>
      <Divider />
      <Stack
        spacing={1}
        useFlexGap>
        <FieldLabel label='Режим оценки результата' />
        <RadioGroup
          value={scoringType}
          onChange={(_, nextValue) =>
            setValue("scoringType", nextValue as Examination_Scoring_Type, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          sx={{ flexDirection: { md: "row" }, flexWrap: "nowrap", gap: 2 }}>
          <FormControlLabel
            value={Examination_Scoring_Type.PassingThreshold}
            disabled={!isEditing}
            sx={{
              alignItems: "flex-start",
              "& .MuiRadio-root": { mt: "-7px" },
              p: 1,
              border: "2px solid",
              borderColor: palette.blue[100],
              borderRadius: 4,
              marginLeft: 0,
              marginRight: 0,
            }}
            control={<Radio />}
            label={
              <Stack spacing={0.5}>
                <Typography sx={{ fontWeight: "600 !important" }}>{SCORING_TYPE_TITLE.PASSING_THRESHOLD}</Typography>
                <Typography>
                  Кандидат набирает балл выше порога&#160;- подтверждает грейд. Ниже&#160;- не&#160;подтверждает.
                </Typography>
              </Stack>
            }
          />
          <FormControlLabel
            value={Examination_Scoring_Type.GradeDetermination}
            disabled={!isEditing}
            sx={{
              alignItems: "flex-start",
              "& .MuiRadio-root": { mt: "-7px" },
              p: 1,
              border: "2px solid",
              borderColor: palette.blue[100],
              borderRadius: 4,
              marginLeft: 0,
              marginRight: 0,
            }}
            control={<Radio />}
            label={
              <Stack spacing={0.5}>
                <Typography sx={{ fontWeight: "600 !important" }}>{SCORING_TYPE_TITLE.GRADE_DETERMINATION}</Typography>
                <Typography>По итоговому баллу система определяет грейд кандидата согласно заданной шкале.</Typography>
              </Stack>
            }
          />
        </RadioGroup>
      </Stack>
      {scoringType === Examination_Scoring_Type.PassingThreshold ? (
        <PassingThresholdControl
          disabled={!isEditing}
          required={scoringType === Examination_Scoring_Type.PassingThreshold}
        />
      ) : (
        <Stack
          spacing={1.5}
          useFlexGap>
          {fields.map((field, index) => (
            <Stack
              key={field.levelId}
              direction='row'
              spacing={1}
              alignItems='flex-start'>
              <TextFieldControl
                name={`gradeDeterminationMap.${index}.from`}
                type='number'
                label='От'
                disabled
                rules={{ required: isGradeDefinitionMode }}
                stackSx={{ width: "20%", minWidth: 100 }}
              />
              <TextFieldControl
                name={`gradeDeterminationMap.${index}.to`}
                type='number'
                label='До'
                disabled={!isEditing}
                onBlurCallback={(event) => handleToValueBlur(index, event)}
                onNumberValueChange={(value) => handleToValueByArrows(index, value)}
                rules={{
                  required: isGradeDefinitionMode,
                  min: {
                    value: Math.min((levels[index]?.from ?? 0) + MIN_LEVEL_DIFF, 100),
                    message: "Минимальная разница между «От» и «До» - 2",
                  },
                  max: {
                    value: 100,
                    message: "Максимальное значение - 100",
                  },
                  validate: (value) => {
                    const normalizedValue = Number(value);
                    const currentFrom = Number(levels[index]?.from ?? 0);
                    if (Number.isNaN(normalizedValue)) return "Введите число";
                    if (normalizedValue - currentFrom < MIN_LEVEL_DIFF) {
                      return "Минимальная разница между «От» и «До» - 2";
                    }
                    if (index === fields.length - 1 && normalizedValue !== 100) {
                      return "Последний уровень должен заканчиваться на 100";
                    }
                    return true;
                  },
                }}
                InputProps={{
                  inputProps: {
                    min: Math.min((levels[index]?.from ?? 0) + MIN_LEVEL_DIFF, 100),
                    max: 100,
                  },
                }}
                stackSx={{ width: "20%", minWidth: 100 }}
              />
              <TextFieldControl
                name={`gradeDeterminationMap.${index}.label`}
                label='Название уровня'
                disabled={!isEditing}
                rules={{
                  required: isGradeDefinitionMode,
                  maxLength: {
                    value: 50,
                    message: "Максимальная длина - 50 символов",
                  },
                }}
                InputProps={{
                  sx: {
                    borderLeft: "3px solid",
                    borderColor: levels[index]?.color || palette.text.primary,
                  },
                }}
                stackSx={{ width: "60%" }}
              />
              <IconButton
                disabled={!isEditing || fields.length <= MIN_ASSESSMENT_LEVELS}
                sx={{ mt: "20px !important" }}
                onClick={() => handleDeleteLevel(index)}>
                <DeleteOutline />
              </IconButton>
            </Stack>
          ))}
          <Button
            variant='outlined'
            fullWidth
            disabled={!isEditing || !canAddLevel || fields.length >= MAX_ASSESSMENT_LEVELS}
            startIcon={<Add />}
            onClick={handleAddLevel}>
            Добавить уровень
          </Button>
          <Stack
            spacing={1}
            sx={{ mt: 1 }}>
            <Stack
              direction='row'
              height={16}
              sx={{
                width: "100%",
                borderRadius: 2,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
              }}>
              {levels.map((level) => (
                <Box
                  key={`progressbar-${level.from}-${level.to}-${level.color}-${level.label || "empty"}`}
                  title={getLevelTitle(level)}
                  sx={{
                    width: getSegmentWidth(level.from, level.to),
                    backgroundColor: level.color,
                  }}
                />
              ))}
            </Stack>
            <Stack
              direction='row'
              flexWrap='wrap'
              columnGap={2}
              rowGap={1}>
              {levels.map((level) => (
                <Stack
                  key={`label-${level.from}-${level.to}-${level.color}-${level.label || "empty"}`}
                  direction='row'
                  alignItems='center'
                  spacing={1}>
                  <Box
                    sx={{
                      backgroundColor: level.color,
                      width: "16px",
                      height: "16px",
                      borderRadius: "100%",
                    }}
                  />
                  <Typography>{getLevelTitle(level)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default ResultAssessmentModeControlProps;
